import React, { useEffect, useState, useContext } from 'react';
import { Layout } from '../components/Layout/Layout';
import apiClient from '../api';
import { AuthContext } from '../context/AuthContext';
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  Select,
  MenuItem,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Snackbar,
  Alert,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import { Delete, AdminPanelSettings, PersonAdd } from '@mui/icons-material';

interface IAdminUser {
  _id: string;
  username: string;
  role: string;
  name?: string;
}

export const SuperAdminPage: React.FC = () => {
  const { userId } = useContext(AuthContext);
  const [adminUsers, setAdminUsers] = useState<IAdminUser[]>([]);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('admin');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchAdminUsers = async () => {
    try {
      const { data } = await apiClient.get('/admin');
      setAdminUsers(data.result.adminUsers);
    } catch (error) {
      console.error(error);
      setError('Failed to fetch admin users.');
    }
  };

  useEffect(() => {
    fetchAdminUsers();
  }, []);

  const handleCreateUser = async () => {
    if (!username || !password || !name) {
      setError('Please fill in all required fields.');
      return;
    }

    try {
      const { data } = await apiClient.post('/admin', {
        username,
        password,
        name,
        role,
      });
      if (data?.result?.adminUser) {
        setUsername('');
        setPassword('');
        setName('');
        setRole('admin');
        setSuccess('Admin user created successfully.');
        await fetchAdminUsers();
      }
    } catch (error: any) {
      console.error(error);
      setError(error.response?.data?.message || 'Failed to create admin user.');
    }
  };

  const handleDeleteUser = async (adminUserId: string) => {
    if (adminUserId === userId) {
      setError("You cannot delete your own account.");
      return;
    }

    try {
      await apiClient.delete(`/admin/${adminUserId}`);
      setAdminUsers((prev) => prev.filter((u) => u._id !== adminUserId));
      setSuccess('Admin user deleted successfully.');
    } catch (error: any) {
      console.error(error);
      setError(error.response?.data?.message || 'Failed to delete admin user.');
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AdminPanelSettings sx={{ color: '#fff', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#0f172a' }}>
                Super Admin
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Manage administrator accounts and permissions
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Create Admin User Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <PersonAdd sx={{ color: '#f59e0b', fontSize: 20 }} />
              <Typography variant="h6" fontWeight={600}>
                Create Admin User
              </Typography>
            </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                label="Username"
                variant="outlined"
                fullWidth
                size="small"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                label="Password"
                type="password"
                variant="outlined"
                fullWidth
                size="small"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                label="Name"
                variant="outlined"
                fullWidth
                size="small"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Select
                displayEmpty
                value={role}
                onChange={(e) => setRole(e.target.value)}
                fullWidth
                size="small"
              >
                <MenuItem value="" disabled>
                  Select Role
                </MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="super-admin">Super Admin</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ height: '100%' }}
                onClick={handleCreateUser}
                startIcon={<PersonAdd />}
              >
                Create
              </Button>
            </Grid>
          </Grid>
          </CardContent>
        </Card>

        {/* Admin Users Table */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
              All Admin Users
            </Typography>
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Username</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {adminUsers.length > 0 ? (
                      adminUsers.map((user) => (
                        <TableRow key={user._id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {user.username}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.role}
                              size="small"
                              color={user.role === 'super-admin' ? 'warning' : 'primary'}
                              variant="filled"
                            />
                          </TableCell>
                          <TableCell>{user.name}</TableCell>
                          <TableCell align="center">
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteUser(user._id)}
                              disabled={user._id === userId}
                              size="small"
                              title={
                                user._id === userId
                                  ? "You cannot delete your own account"
                                  : "Delete User"
                              }
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            No admin users found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </CardContent>
        </Card>
      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Layout>
  );
};